import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ImageOff, Loader2, PenTool, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DesignDialog } from "@/components/designer/DesignButton";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { useLines } from "@/lib/components";
import { money } from "@/lib/pricing";
import { Photo, usePhoto } from "@/components/quote/ProductPicker";

const BADGE = { high: ["High", "bg-[#34C759]/15 text-[#248A3D]", CheckCircle2], check: ["Check match", "bg-[#FF9500]/15 text-[#8A4B00]", AlertTriangle],
  unmatched: ["Unmatched", "bg-[#FF3B30]/12 text-[#D70015]", AlertTriangle], designer: ["Build in Designer", "bg-[#007AFF]/12 text-[#0062CC]", PenTool] };

function Option({ o, testId, onPick, busy }) {
  return (
    <button data-testid={testId} disabled={busy} onClick={() => onPick(o)}
      className="flex w-full items-center gap-3 border-b border-black/[0.08] px-3 py-2.5 text-left last:border-0 hover:bg-black/[0.03] disabled:opacity-50">
      {o.photo_key ? <Photo k={o.photo_key} className="h-12 w-12 shrink-0" /> : <div className="h-12 w-12 shrink-0 rounded-md bg-black/[0.04]" />}
      <div className="min-w-0"><p className="truncate text-[15px] font-medium text-black">{o.line_name}</p>
        <p className="truncate text-[13px] text-[#6D6D72]">{o.catalog_name || o.category.replace(/_/g, " ")}</p></div>
    </button>
  );
}

function MatchPicker({ row, idx, onClose }) {
  const { quoteId, applyServer, prepare } = useLines();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/quotes/${quoteId}/items/${row.id}/match-options`, { params: { q } }).then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e))), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, quoteId, row.id]);
  const pick = async (o) => {
    setBusy(true);
    try {
      if (prepare) await prepare();
      const { data } = await api.post(`/quotes/${quoteId}/items/${row.id}/match`, { product_line_id: o.product_line_id, catalog_id: o.catalog_id });
      applyServer(data);
      toast.success(`Line ${idx + 1} matched to ${o.line_name}`);
      onClose();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`match-picker-${idx}`} className="max-w-lg">
        <DialogHeader><DialogTitle className="text-[22px] font-bold">Change match</DialogTitle>
          <DialogDescription>{row.raw_type || row.type_name} · {row.width_in}×{row.height_in} in. The line is repriced at once; a typed price stays.</DialogDescription></DialogHeader>
        <p className="px-1 text-[13px] uppercase text-[#6D6D72]">Suggestions</p>
        <div className="overflow-hidden rounded-[12px] bg-white">
          {d?.suggestions.map((o, i) => <Option key={`${o.product_line_id}-${i}`} o={o} busy={busy} testId={`match-suggestion-${idx}-${i}`} onPick={pick} />)}
          {!d && <Loader2 className="m-4 h-5 w-5 animate-spin text-[#007AFF]" />}
        </div>
        <label className="relative block"><Search className="absolute left-3 top-3 h-4 w-4 text-[#8E8E93]" />
          <input data-testid={`match-search-${idx}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all products"
            className="h-10 w-full rounded-[10px] border border-black/10 bg-white pl-9 pr-3 text-[15px] outline-none focus:border-[#007AFF]" /></label>
        {q && <div className="max-h-64 overflow-y-auto rounded-[12px] bg-white">
          {d?.results.map((o, i) => <Option key={`${o.product_line_id}-${o.catalog_id}-${i}`} o={o} busy={busy} testId={`match-result-${idx}-${i}`} onPick={pick} />)}
          {d && !d.results.length && <p className="px-4 py-5 text-center text-[15px] text-[#6D6D72]">No products found.</p>}
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export function MatchChip({ row, idx, locked, onPatch, types = [] }) {
  const { quoteId } = useLines();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [build, setBuild] = useState(false);
  if (!row.match_status) return null;
  const [label, cls, Icon] = BADGE[row.match_status] || BADGE.check;
  if (row.match_status === "designer") {
    return (
      <div data-testid={`quote-item-match-${idx}`} className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${cls}`} data-testid={`quote-item-match-status-${idx}`}><Icon className="h-3.5 w-3.5" />{label}</span>
        <span className="text-[#3C3C43]" data-testid={`quote-item-flag-reason-${idx}`}>
          Why flagged: {row.match_label?.startsWith("Combined") ? row.match_label : "Combined unit"} · {row.width_in}×{row.height_in} in · $0 until built
        </span>
        {!locked && onPatch && <button data-testid={`quote-item-open-designer-${idx}`} onClick={() => setBuild(true)} className="font-medium text-[#0062CC]">Open in Designer</button>}
        {build && <DesignDialog row={row} idx={idx} onPatch={onPatch} owner={user?.role === "owner"} types={types} onClose={() => setBuild(false)} />}
      </div>
    );
  }
  return (
    <div data-testid={`quote-item-match-${idx}`} className="flex flex-wrap items-center gap-2 text-[13px]">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${cls}`} data-testid={`quote-item-match-status-${idx}`}><Icon className="h-3.5 w-3.5" />{label}</span>
      {row.match_status !== "unmatched" && <span className="text-[#3C3C43]" data-testid={`quote-item-match-label-${idx}`}>Matched: {row.match_label}</span>}
      {row.their_price != null && <span data-testid={`quote-item-their-price-${idx}`} className="rounded-full bg-black/[0.05] px-2 py-0.5 text-[#3C3C43]">Their price {money(row.their_price)}</span>}
      {quoteId && !locked && <button data-testid={`quote-item-change-match-${idx}`} onClick={() => setOpen(true)} className="font-medium text-[#0062CC]">
        {row.match_status === "unmatched" ? "Pick product" : "Change match"}</button>}
      {open && <MatchPicker row={row} idx={idx} onClose={() => setOpen(false)} />}
    </div>
  );
}

export function LinePhoto({ k, idx, className }) {
  const [v, setV] = useState(0);
  const url = usePhoto(k, v);
  const [st, setSt] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    if (url) return undefined;
    let live = true;
    let tries = 0;
    const poll = () => api.get(`/catalog/photos/${k}/status`).then((r) => {
      if (!live) return;
      tries += 1;
      const waiting = ["queued", "generating", "missing"].includes(r.data.status);
      setSt(waiting && tries > 12 ? "failed" : r.data.status);
      if (r.data.ready) setV((x) => x + 1);
      else if (waiting && tries <= 12) timer.current = setTimeout(poll, 6000);
    }).catch(() => live && setSt("failed"));
    timer.current = setTimeout(poll, 800);
    return () => { live = false; clearTimeout(timer.current); };
  }, [k, url, v]);
  if (url) return <Photo k={k} version={v} className={className} testId={`quote-item-photo-${idx}`} />;
  const retry = async () => { try { const { data } = await api.post(`/catalog/photos/${k}/retry`); setSt(data.status); setV((x) => x + 1); } catch (e) { toast.error(errorMessage(e)); } };
  return (
    <div data-testid={`quote-item-photo-${idx}`} className={`grid place-items-center rounded-md bg-white p-3 text-center text-[13px] text-[#6D6D72] ${className}`}>
      {st === "failed" ? <div><ImageOff className="mx-auto mb-1 h-5 w-5" />Photo unavailable —
        <button data-testid={`quote-item-photo-retry-${idx}`} onClick={retry} className="ml-1 inline-flex items-center gap-1 font-medium text-[#0062CC]"><RefreshCw className="h-3.5 w-3.5" />Retry</button></div>
        : <div><Loader2 className="mx-auto mb-1 h-5 w-5 animate-spin text-[#007AFF]" />Making a matching photo…</div>}
    </div>
  );
}

export function MatchBanner({ rows }) {
  const n = rows.filter((r) => ["check", "unmatched", "designer"].includes(r.match_status)).length;
  if (!n) return null;
  return (
    <div data-testid="match-check-banner" className="flex items-center gap-2 rounded-[12px] bg-[#FF9500]/10 px-4 py-3 text-[15px] text-[#8A4B00]">
      <AlertTriangle className="h-4 w-4" /> {n} line{n === 1 ? "" : "s"} need{n === 1 ? "s" : ""} a product check
    </div>
  );
}
